import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, UserPlus } from "../icons";
import Alert from "../Alert";
import { apiClient } from "../../lib/api";
import { useFormValidation } from "../../hooks/useFormValidation";
import { quickCreatePlayerSchema } from "../../lib/validationSchemas";
import { applyBackendValidationErrors } from "../../lib/backendErrorUtils";
import { usePositionReferenceOptions } from "../../hooks/usePositionReferenceOptions";
import type { PlayerPosition } from "../../types";
import {
  Select,
  SelectContent,
  SelectEmpty,
  SelectItem,
  SelectSearchInput,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useSelectFilter } from "../ui/useSelectFilter";

// EPIC-08 Phase 2: minimal quick-create form (name + jersey + position only).
// Deliberately does NOT reuse the full `playerSchema`/PlayersManager form —
// that form requires birth_date/country by design and stays byte-identical
// for non-provisional flows. The atomic backend endpoint creates a
// provisional Player and attaches it to `teamId`'s roster in one call.
// Position codes come from /api/v1/reference-data (PMA-REF) — no current
// value to preserve here (always a brand-new player), so a flat active list
// is enough, no optgroup-by-tactical-group needed.

interface QuickCreateFormData {
  name: string;
  jersey_number: number | "";
  position: PlayerPosition | "";
}

const INITIAL_FORM_DATA: QuickCreateFormData = {
  name: "",
  jersey_number: "",
  position: "",
};

export interface QuickCreatePlayerModalProps {
  teamId: string;
  teamName?: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}

/** Radix `Select.Item` forbids an empty-string value (reserved for
 *  "nothing selected") — same sentinel pattern as `ReferenceValueSelect`. */
const UNSET = "__unset__";

export default function QuickCreatePlayerModal({
  teamId,
  teamName,
  isOpen,
  onClose,
  onCreated,
}: QuickCreatePlayerModalProps) {
  const { t } = useTranslation("admin");
  const [formData, setFormData] =
    useState<QuickCreateFormData>(INITIAL_FORM_DATA);
  const positionOptions = usePositionReferenceOptions(formData.position);
  const flatPositionOptions = positionOptions.groupOrder.flatMap(
    (group) => positionOptions.grouped[group],
  );
  const positionSelectOptions = flatPositionOptions.map((opt) => ({
    value: opt.code,
    label: opt.label,
  }));
  const {
    query: positionQuery,
    setQuery: setPositionQuery,
    visible: visiblePositionOptions,
  } = useSelectFilter(positionSelectOptions);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    validateForm,
    validateAndSetFieldError,
    clearErrors,
    getFieldError,
    setFieldError,
  } = useFormValidation<QuickCreateFormData>(quickCreatePlayerSchema, t);

  if (!isOpen) {
    return null;
  }

  const resetAndClose = () => {
    setFormData(INITIAL_FORM_DATA);
    setFormError(null);
    clearErrors();
    setSubmitting(false);
    onClose();
  };

  // Backend surfaces team-missing (404), jersey-conflict (409), and
  // roster-cap (400) failures as a plain-string `detail`, e.g.
  // "Jersey number 7 already taken" / "Team roster limit of 23 players
  // reached" / "Team '<id>' not found" (same convention as the existing
  // roster-add flow in TeamsManager). Route jersey conflicts to the field,
  // everything else to the form-level alert.
  const handleBackendStringError = (detail: string) => {
    const normalized = detail.toLowerCase();
    if (normalized.includes("jersey number")) {
      setFieldError("jersey_number", detail);
      setFormError(t("validation.fixErrors"));
      return;
    }
    setFormError(detail);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (!validateForm(formData)) {
      setFormError(t("validation.fixErrors"));
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      await apiClient.post(`/teams/${teamId}/players/quick-create`, {
        name: formData.name.trim(),
        jersey_number: formData.jersey_number,
        position: formData.position,
      });
      await onCreated();
      resetAndClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      // 422s come back as FastAPI's standard array-of-field-errors shape
      // (same as every other router) — reuse the codebase's standard
      // handler for that before falling back to the 400/404 plain-string
      // shapes this endpoint also returns.
      const handledFieldErrors = applyBackendValidationErrors(detail, {
        setFieldError,
        clearErrors,
        translate: t,
      });
      if (handledFieldErrors) {
        setFormError(t("validation.fixErrors"));
      } else if (typeof detail === "string") {
        handleBackendStringError(detail);
      } else {
        setFormError(t("errorSavingData"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]"
      data-testid="quick-create-player-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-create-player-modal-title"
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
      >
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2
            id="quick-create-player-modal-title"
            className="text-lg font-semibold text-gray-900 flex items-center gap-2"
          >
            <UserPlus className="h-5 w-5 text-primary-600" aria-hidden="true" />
            {teamName
              ? t("quickCreatePlayer.titleWithTeam", { team: teamName })
              : t("quickCreatePlayer.title")}
          </h2>
          <button
            type="button"
            onClick={resetAndClose}
            aria-label={t("common:common.close", "Close dialog")}
            className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 transition-colors"
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-xs text-gray-500">{t("quickCreatePlayer.hint")}</p>
          {formError && (
            <Alert tone="error" onDismiss={() => setFormError(null)}>
              {formError}
            </Alert>
          )}
          <div>
            <label
              htmlFor="quick-create-player-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("quickCreatePlayer.nameLabel")} *
            </label>
            <input
              id="quick-create-player-name"
              type="text"
              autoFocus
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                validateAndSetFieldError("name", e.target.value);
              }}
              onBlur={(e) => validateAndSetFieldError("name", e.target.value)}
              placeholder={t("quickCreatePlayer.namePlaceholder")}
              data-testid="quick-create-player-name"
              className={`input w-full ${
                getFieldError("name") ? "border-red-500" : ""
              }`}
            />
            {getFieldError("name") && (
              <p className="mt-1 text-sm text-red-600">
                {getFieldError("name")}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="quick-create-player-jersey"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("jerseyNumber")} *
              </label>
              <input
                id="quick-create-player-jersey"
                type="number"
                min="1"
                max="99"
                value={formData.jersey_number}
                onChange={(e) => {
                  const value = e.target.value
                    ? parseInt(e.target.value, 10)
                    : "";
                  setFormData({ ...formData, jersey_number: value });
                  validateAndSetFieldError(
                    "jersey_number",
                    value === "" ? undefined : value,
                  );
                }}
                onBlur={(e) =>
                  validateAndSetFieldError(
                    "jersey_number",
                    e.target.value ? parseInt(e.target.value, 10) : undefined,
                  )
                }
                data-testid="quick-create-player-jersey"
                className={`input w-full ${
                  getFieldError("jersey_number") ? "border-red-500" : ""
                }`}
              />
              {getFieldError("jersey_number") && (
                <p className="mt-1 text-sm text-red-600">
                  {getFieldError("jersey_number")}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="quick-create-player-position"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("position")} *
              </label>
              <Select
                value={formData.position === "" ? UNSET : formData.position}
                onValueChange={(next) => {
                  const value = (next === UNSET ? "" : next) as
                    | PlayerPosition
                    | "";
                  setFormData({ ...formData, position: value });
                  validateAndSetFieldError(
                    "position",
                    value === "" ? undefined : value,
                  );
                }}
                onOpenChange={(open) => {
                  if (!open) {
                    validateAndSetFieldError(
                      "position",
                      formData.position || undefined,
                    );
                  }
                }}
              >
                <SelectTrigger
                  id="quick-create-player-position"
                  data-testid="quick-create-player-position"
                  className={getFieldError("position") ? "border-red-500" : ""}
                >
                  <SelectValue
                    placeholder={t("quickCreatePlayer.selectPosition")}
                  >
                    {
                      positionSelectOptions.find(
                        (opt) => opt.value === formData.position,
                      )?.label
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value={UNSET}
                    data-testid="quick-create-player-position-option-unset"
                  >
                    {t("quickCreatePlayer.selectPosition")}
                  </SelectItem>
                  <SelectSearchInput
                    value={positionQuery}
                    onChange={(e) => setPositionQuery(e.target.value)}
                    placeholder={t("search")}
                  />
                  {visiblePositionOptions.length === 0 && (
                    <SelectEmpty>{t("pickerNoMatches")}</SelectEmpty>
                  )}
                  {visiblePositionOptions.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`quick-create-player-position-option-${opt.value}`}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {getFieldError("position") && (
                <p className="mt-1 text-sm text-red-600">
                  {getFieldError("position")}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={resetAndClose}
              className="btn btn-secondary"
              disabled={submitting}
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary flex items-center"
              disabled={submitting}
              data-testid="quick-create-player-submit"
            >
              {submitting && (
                <span
                  className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
                  aria-label="saving-spinner"
                />
              )}
              {t("quickCreatePlayer.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
